import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  CircularProgress,
  Typography,
  Box,
  Card,
  Stack,
  Avatar,
  Chip,
  Button,
  Divider,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import moment from 'moment';
import Page from 'src/components/Page';
import CreatedBy from 'src/components/CreatedBy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'src/context/auth/AuthContext';
import { useViewScope } from 'src/hooks/useViewScope';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { transactionService } from 'src/services/transaction.service';
import { studentService } from 'src/services/student.service';

function getStatusBadge(status?: string | null) {
  switch (status) {
    case 'Approved':
      return {
        label: 'Approved & Settled',
        color: '#059669',
        bgcolor: 'rgba(16, 185, 129, 0.12)',
        icon: 'solar:check-circle-bold-duotone',
      };
    case 'Pending':
      return {
        label: 'Pending Approval',
        color: '#D97706',
        bgcolor: 'rgba(245, 158, 11, 0.12)',
        icon: 'solar:hourglass-bold-duotone',
      };
    case 'Rejected':
    case 'Declined':
      return {
        label: 'Rejected',
        color: '#DC2626',
        bgcolor: 'rgba(239, 68, 68, 0.12)',
        icon: 'solar:close-circle-bold-duotone',
      };
    default:
      return {
        label: status || 'Completed',
        color: '#059669',
        bgcolor: 'rgba(16, 185, 129, 0.12)',
        icon: 'solar:check-circle-bold-duotone',
      };
  }
}

/**
 * Format arbitrary nested metadata objects into elegant human-readable UI elements
 */
function FormattedMetadataItem({ label, value }: { label: string; value: any }): JSX.Element {
  const formattedLabel = label
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim();

  if (value === null || value === undefined || value === '') {
    return (
      <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'capitalize' }}>
          {formattedLabel}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled', mt: 0.25, fontStyle: 'italic', fontSize: '0.8125rem' }}>
          Not specified
        </Typography>
      </Box>
    );
  }

  // Array of strings or numbers -> Tag Chips
  if (Array.isArray(value)) {
    return (
      <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'capitalize', display: 'block', mb: 0.75 }}>
          {formattedLabel}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {value.map((v, idx) => (
            <Chip
              key={idx}
              label={typeof v === 'object' ? JSON.stringify(v) : String(v)}
              size="small"
              sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // Nested Object -> Key-value list
  if (typeof value === 'object') {
    return (
      <Box sx={{ p: 1.75, borderRadius: 1.5, bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'capitalize', display: 'block', mb: 1 }}>
          {formattedLabel}
        </Typography>
        <Stack spacing={0.75}>
          {Object.entries(value).map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize', fontWeight: 500 }}>
                {k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem' }}>
                {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  // Boolean
  if (typeof value === 'boolean') {
    return (
      <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'capitalize' }}>
          {formattedLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: value ? 'success.main' : 'text.secondary', mt: 0.25 }}>
          {value ? 'Yes / Active' : 'No / Inactive'}
        </Typography>
      </Box>
    );
  }

  // Simple string / number
  return (
    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'capitalize' }}>
        {formattedLabel}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.25, fontSize: '0.8125rem' }}>
        {String(value)}
      </Typography>
    </Box>
  );
}

export default function ViewStreetCent(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getBaseRoute, user, hasPermission } = useAuth();
  const { isSelfScoped: isStudent } = useViewScope('transactions');
  const backRoute = getBaseRoute('/street-cents');
  const queryClient = useQueryClient();

  const [copied, setCopied] = useState(false);
  const [confirmCollect, setConfirmCollect] = useState(false);

  const collectMutation = useMutation({
    mutationFn: () => transactionService.collectTransaction(id as string, 'Collected by staff'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactionDetails', id] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Charge collected from the scholar balance');
      setConfirmCollect(false);
    },
    onError: (err: Error) => toast.error(err.message || 'Could not collect this charge'),
  });

  // Resolve student ID if authenticated user is a student
  const { data: currentStudentId } = useQuery({
    queryKey: ['studentIdByUserId', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: isStudent && !!user?.id,
  });

  const { data: transaction, isLoading, error } = useQuery({
    queryKey: ['transactionDetails', id],
    queryFn: () => transactionService.getTransactionById(id as string),
    enabled: Boolean(id),
  });

  const transactionType = transaction?.transaction_type;
  const isCredit = transactionType?.type === 'credit';
  const amount = Number(transaction?.amount || 0);
  const balanceAfter = Number(transaction?.balanceafter ?? transaction?.student?.balance ?? 0);
  const preBalance = isCredit ? balanceAfter - amount : balanceAfter + amount;
  const statusMeta = getStatusBadge(transaction?.status);

  const student = transaction?.student;
  const scholarId = transaction?.studentid || student?.id;
  const scholarName = `${student?.user?.firstname || ''} ${student?.user?.lastname || ''}`.trim() || 'Unnamed Scholar';
  const scholarEmail = student?.user?.email || 'No email registered';
  const scholarGrade = student?.grade ? `Grade ${student.grade}` : 'Scholar';
  const scholarCurrentBalance = Number(student?.balance || 0);

  // Staff can pay an open charge for the scholar from the scholar's balance.
  const isOpen = ['PENDING_STUDENT_ACTION', 'DEFERRED', 'QUESTIONED'].includes(transaction?.transaction_status ?? '');
  const canCollect = !isStudent && isOpen && hasPermission('transactions.create');
  const balanceAfterCollect = scholarCurrentBalance + (isCredit ? amount : -amount);

  const viewStudentPath = scholarId ? getBaseRoute(`/view-student/${scholarId}`) : null;

  useHeaderBreadcrumbs({
    backUrl: backRoute,
    backLabel: isStudent ? 'My S.T.R.E.E.T Cents' : 'S.T.R.E.E.T Cents',
    breadcrumbs: [
      { label: isStudent ? 'My S.T.R.E.E.T Cents' : 'S.T.R.E.E.T Cents', path: backRoute },
      { label: transactionType?.name || 'Transaction Details' },
    ],
  });

  const handleCopyId = () => {
    if (transaction?.id) {
      navigator.clipboard.writeText(transaction.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <Page title="Transaction Details | Gold Minds Portal">
        <Container maxWidth="lg" sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
            Loading transaction ledger record...
          </Typography>
        </Container>
      </Page>
    );
  }

  if (error || !transaction) {
    return (
      <Page title="Transaction Details | Gold Minds Portal">
        <Container maxWidth="lg" sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 600 }}>
            Transaction Record Not Found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
            The transaction you are trying to view does not exist or may have been deleted.
          </Typography>
          <Button variant="contained" onClick={() => navigate(backRoute)} sx={{ fontWeight: 600, textTransform: 'none' }}>
            Back to {isStudent ? 'My Street Cents' : 'Street Cents Ledger'}
          </Button>
        </Container>
      </Page>
    );
  }

  // Security guard: Ensure students cannot view other scholars' transaction records
  const isUnauthorizedStudent = isStudent && !!currentStudentId && scholarId && scholarId !== currentStudentId;
  if (isUnauthorizedStudent) {
    return (
      <Page title="Unauthorized | Gold Minds Portal">
        <Container maxWidth="lg" sx={{ py: 10, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(239, 68, 68, 0.12)',
              color: '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Icon icon="solar:shield-warning-bold-duotone" width={32} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
            Access Restricted
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 450, mx: 'auto', mb: 3 }}>
            You do not have permission to view this transaction ledger record. You can only view transactions associated with your own scholar wallet.
          </Typography>
          <Button variant="contained" onClick={() => navigate(backRoute)} sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
            Back to My Street Cents
          </Button>
        </Container>
      </Page>
    );
  }

  // Parse savings allocation if available
  const savingsAllocation = transaction.savings_allocation as Record<string, number> | null;
  const hasSavingsAllocation = savingsAllocation && Object.keys(savingsAllocation).length > 0;

  // Filter extended properties to render cleanly
  const extendedProps = (transaction.extendedproperties || {}) as Record<string, any>;
  const hasExtendedProps = Object.keys(extendedProps).length > 0;

  return (
    <Page title={`${transactionType?.name || 'Transaction'} | Gold Minds Portal`}>
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5 }}>
        {/* Header Ribbon */}
        <Box sx={{ mb: 2.5 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<Icon icon="solar:arrow-left-linear" width={16} />}
            onClick={() => navigate(backRoute)}
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.8125rem',
              p: 0,
              mb: 1.5,
              '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
            }}
          >
            {isStudent ? 'Back to My Street Cents' : 'Back to Street Cents Ledger'}
          </Button>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em', fontSize: '1.375rem' }}>
                  {transactionType?.name || 'Ledger Transaction'}
                </Typography>
                <Chip
                  icon={<Icon icon={statusMeta.icon} width={13} />}
                  label={statusMeta.label}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: statusMeta.color,
                    bgcolor: statusMeta.bgcolor,
                    border: `1px solid ${statusMeta.color}22`,
                    '& .MuiChip-icon': {
                      color: `${statusMeta.color} !important`,
                      marginLeft: '4px',
                    },
                  }}
                />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                  Transaction ID: <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{transaction.id}</span>
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy Transaction ID'}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleCopyId}
                    sx={{
                      p: 0.25,
                      minWidth: 'auto',
                      fontSize: '0.75rem',
                      color: copied ? 'success.main' : 'text.secondary',
                    }}
                  >
                    <Icon icon={copied ? 'solar:check-bold' : 'solar:copy-linear'} width={14} />
                  </Button>
                </Tooltip>
                {!isStudent && (
                  <>
                    <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>
                      ·
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                      Created by
                    </Typography>
                    <CreatedBy creator={transaction.creator} />
                  </>
                )}
              </Stack>
            </Box>
            {canCollect && (
              <Button
                variant="contained"
                startIcon={<Icon icon="solar:wallet-money-bold-duotone" width={18} />}
                onClick={() => setConfirmCollect(true)}
                sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5, boxShadow: 'none' }}
              >
                Collect Now
              </Button>
            )}
          </Stack>
        </Box>

        <Dialog open={confirmCollect} onClose={() => setConfirmCollect(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Collect this charge now?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {`$${amount.toFixed(2)} will be taken from ${scholarName}'s balance: $${scholarCurrentBalance.toFixed(2)} → $${balanceAfterCollect.toFixed(2)}.`}
            </Typography>
            {balanceAfterCollect < 0 && (
              <Typography variant="body2" sx={{ color: 'error.main', mt: 1, fontWeight: 600 }}>
                This takes the balance below zero.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmCollect(false)} sx={{ textTransform: 'none' }}>
              Keep Pending
            </Button>
            <Button
              variant="contained"
              onClick={() => collectMutation.mutate()}
              disabled={collectMutation.isPending}
              sx={{ textTransform: 'none', boxShadow: 'none' }}
            >
              {collectMutation.isPending ? 'Collecting...' : 'Collect Now'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 2-Column Responsive Layout */}
        <Grid container spacing={2.5}>
          {/* Left Column: Transaction Details & Breakdown */}
          <Grid item xs={12} md={7.5}>
            <Stack spacing={2.5}>
              {/* Financial Flow Card */}
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, fontSize: '0.9375rem' }}>
                  Transaction Overview
                </Typography>

                {/* Big Amount Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: isCredit ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)',
                    border: '1px solid',
                    borderColor: isCredit ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    mb: 2.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase' }}>
                        Transaction Value
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          color: isCredit ? '#059669' : '#DC2626',
                          mt: 0.25,
                          fontSize: '1.875rem',
                        }}
                      >
                        {isCredit ? '+' : '-'}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25, display: 'block', fontSize: '0.75rem' }}>
                        {isCredit
                          ? isStudent
                            ? 'Credit deposit added to your wallet'
                            : 'Credit deposit credited to scholar wallet'
                          : isStudent
                          ? 'Debit deduction charged from your balance'
                          : 'Debit deduction charged from scholar balance'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: isCredit ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: isCredit ? '#059669' : '#DC2626',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon
                        icon={isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                        width={26}
                      />
                    </Box>
                  </Stack>
                </Paper>

                {/* Ledger Waterfall Breakdown Grid */}
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Ledger Balance Flow
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {isStudent ? 'Starting Balance' : 'Pre-Transaction Balance'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.25, fontSize: '0.875rem' }}>
                        ${preBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: isCredit ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)' }}>
                      <Typography variant="caption" sx={{ color: isCredit ? '#059669' : '#DC2626', fontWeight: 600 }}>
                        Delta Impact
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isCredit ? '#059669' : '#DC2626', mt: 0.25, fontSize: '0.875rem' }}>
                        {isCredit ? '+' : '-'}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {isStudent ? 'New Balance' : 'Post-Transaction Balance'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mt: 0.25, fontSize: '0.875rem' }}>
                        ${balanceAfter.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Description & Narrative Notes */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: '0.875rem' }}>
                  Description & Context
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.875rem', fontWeight: 400 }}>
                  {transaction.description || transactionType?.description || 'No description recorded for this transaction.'}
                </Typography>

                {transaction.reason && (
                  <Box sx={{ mt: 2, p: 1.75, borderRadius: 1.5, bgcolor: 'background.neutral', borderLeft: '3px solid', borderColor: isCredit ? '#059669' : '#DC2626' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                      Specific Incident / Reason Notes:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.5, fontWeight: 500, lineHeight: 1.5 }}>
                      {transaction.reason}
                    </Typography>
                  </Box>
                )}
              </Card>

              {/* Savings & Wealth Allocation Breakdown (if present) */}
              {hasSavingsAllocation && (
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: 'rgba(124, 58, 237, 0.12)',
                        color: '#7C3AED',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon="solar:safe-square-bold-duotone" width={16} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                      Automated Savings & Wealth Accumulation
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.5}>
                    {Object.entries(savingsAllocation).map(([fundName, allocationValue]) => (
                      <Grid item xs={12} sm={4} key={fundName}>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'capitalize' }}>
                            {fundName.replace(/([A-Z])/g, ' $1')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#7C3AED', mt: 0.25 }}>
                            {typeof allocationValue === 'number' && allocationValue <= 100
                              ? `${allocationValue}% (${((amount * allocationValue) / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })})`
                              : `$${Number(allocationValue).toFixed(2)}`}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              )}

              {/* Extended Properties & Structured Context Card */}
              {hasExtendedProps && (
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: 'rgba(37, 99, 235, 0.12)',
                        color: '#2563EB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon="solar:clipboard-list-bold-duotone" width={16} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                      Classroom & Incident Metadata
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.5}>
                    {Object.entries(extendedProps).map(([key, val]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <FormattedMetadataItem label={key} value={val} />
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              )}
            </Stack>
          </Grid>

          {/* Right Column: Scholar Profile & Audit Trail */}
          <Grid item xs={12} md={4.5}>
            <Stack spacing={2.5}>
              {/* Scholar Information Card */}
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, fontSize: '0.9375rem' }}>
                  {isStudent ? 'Account Information' : 'Scholar Information'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: 'primary.light',
                      color: 'primary.dark',
                      fontWeight: 700,
                      fontSize: '1rem',
                    }}
                  >
                    {scholarName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }}>
                      {scholarName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', fontWeight: 400 }}>
                      {scholarEmail}
                    </Typography>
                    <Chip label={scholarGrade} size="small" sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 600, mt: 0.5 }} />
                  </Box>
                </Stack>

                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50', mb: !isStudent && viewStudentPath ? 2 : 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    {isStudent ? 'Your Current Balance' : 'Scholar Current Balance'}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.25, fontSize: '1.125rem' }}>
                    ${scholarCurrentBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                {/* View Scholar Financial Profile (Educators Only) */}
                {!isStudent && viewStudentPath && (
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    startIcon={<Icon icon="solar:user-bold" width={16} />}
                    onClick={() => navigate(viewStudentPath)}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 1.5,
                      textTransform: 'none',
                    }}
                  >
                    View Scholar Financial Profile
                  </Button>
                )}
              </Card>

              {/* Audit & Compliance Card */}
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, fontSize: '0.9375rem' }}>
                  {isStudent ? 'Transaction Record' : 'Audit & Ledger Metadata'}
                </Typography>
                <Stack spacing={1.5}>
                  {!isStudent && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Created By
                      </Typography>
                      <CreatedBy creator={transaction.creator} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Logged Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem' }}>
                      {transaction.createdat ? moment(transaction.createdat).format('MMM D, YYYY · h:mm A') : '--'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Last Settlement
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem' }}>
                      {transaction.updatedat ? moment(transaction.updatedat).format('MMM D, YYYY · h:mm A') : '--'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Payment Channel
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem' }}>
                      {transaction.payment_method || 'Classroom Virtual Wallet'}
                    </Typography>
                  </Box>
                  {/* Internal Teacher Override Reason (Educators Only) */}
                  {!isStudent && transaction.teacher_override_reason && (
                    <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                      <Typography variant="caption" sx={{ color: '#D97706', fontWeight: 600, display: 'block' }}>
                        Teacher Override Reason:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.25, fontSize: '0.8125rem', fontWeight: 500 }}>
                        {transaction.teacher_override_reason}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
